import { useState, useEffect, useRef } from 'react';
import { useDebounce } from '../hooks';

/**
 * Generic hook that reloads whenever the dimensions of a DOM element change. Accepts a debounce duration.
 *
 * The dimensionsUpdating property is useful to temporarily hide content until new dimensions
 * are available to use to rerender the content. But debouncing the update of the actual dimensions prevents
 * excessive rerenders.
 *
 * @param {Element} element - any DOM element that can have a ResizeObserver
 * @param {Number} debounceDuration - how long to debounce before returning a new set of dimensions
 * @returns {Object} object with dimensions and a boolean for whether the debounce is waiting
 */
export default function useDimensions(element, debounceDuration = 400) {
  // Store the previously observed element in order to unobserve it if it changes.
  const previousObservedElement = useRef(null);

  // Dimensions will be up-to-date at all times within the context of this hook.
  const [dimensions, setDimensions] = useState(null);

  // Observer never needs to change.
  const observerRef = useRef(
    new ResizeObserver(([{ target }]) => {
      setDimensions([target.offsetWidth, target.offsetHeight]);
    }),
  );

  const debounce = useDebounce(dimensions, debounceDuration);

  // This will only run once unless the element changes, in which case it should reattach to the new element.
  useEffect(() => {
    const observer = observerRef.current;
    setDimensions(element ? [element.offsetWidth, element.offsetHeight] : null);

    if (element) {
      observer.observe(element);
      previousObservedElement.current = element;
    }

    return () => {
      if (previousObservedElement.current) {
        observer.unobserve(previousObservedElement.current);
      }
    };
  }, [element]);

  return {
    dimensions: debounce.value,
    dimensionsUpdating: debounce.isWaiting,
  };
}
